import { Box, Typography, Link, Stack } from "@mui/material";
import logoNoText from "../images/logo-notext.png";
import {
  ACCENT,
  ACCENT_DARK,
  SURFACE_MUTED,
  HAIRLINE,
  INK_SECONDARY,
  INK_MUTED,
  FONT,
  WEIGHT,
} from "../theme/tokens";

/**
 * Global application footer.
 *
 * A quiet, single-row footer that sits on the app's light slate surface
 * (a hairline-bordered band rather than a heavy dark bar) so it reads as part
 * of the same design system as the rest of the product. Collapses to a stacked
 * layout on narrow viewports.
 */
const Footer = () => {
  const year = new Date().getFullYear();

  // Placeholder destinations — Privacy/Terms have no dedicated pages yet, so
  // they render as accessible buttons (no navigation jump) until those routes
  // exist; "Contact us" opens the team mailbox.
  const links = [
    { label: "Privacy Policy", component: "button", type: "button" },
    { label: "Terms of Service", component: "button", type: "button" },
    { label: "Contact us", href: "mailto:contact@efabric.io" },
  ];

  return (
    <Box
      component="footer"
      sx={{
        flexShrink: 0,
        mt: "auto",
        width: "100%",
        // Slate-100 band — a step darker than the page canvas so the footer
        // reads as a distinct band rather than blending into the background.
        backgroundColor: SURFACE_MUTED,
        borderTop: `1px solid ${HAIRLINE}`,
        px: { xs: 2, sm: 4 },
        py: 1.5,
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
      }}
    >
      {/* Brand + copyright */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <img
          src={logoNoText}
          alt="EFabric"
          style={{ height: 18, display: "block", opacity: 0.9 }}
        />
        <Typography
          component="span"
          sx={{
            fontWeight: WEIGHT.semibold,
            fontSize: FONT.sm,
            color: ACCENT_DARK,
            letterSpacing: "0.01em",
          }}
        >
          EFabric
        </Typography>
        <Typography
          component="span"
          sx={{ fontSize: FONT.xs, color: INK_MUTED }}
        >
          © {year} · All rights reserved.
        </Typography>
      </Box>

      {/* Links */}
      <Stack direction="row" spacing={{ xs: 2, sm: 3 }} alignItems="center">
        {links.map((link) => (
          <Link
            key={link.label}
            href={link.href}
            component={link.component}
            type={link.type}
            underline="none"
            sx={{
              fontSize: FONT.sm,
              fontWeight: WEIGHT.medium,
              color: INK_SECONDARY,
              cursor: "pointer",
              transition: "color 0.15s ease",
              "&:hover": { color: ACCENT },
            }}
          >
            {link.label}
          </Link>
        ))}
      </Stack>
    </Box>
  );
};

export default Footer;
